import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ArrowRight } from "lucide-react";

const GoogleButton = ({ label }) => (
  <button
    type="button"
    data-testid="auth-google-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/portal";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="w-full border border-border py-2.5 rounded flex items-center justify-center gap-2 text-sm font-medium hover:border-foreground transition-colors bg-background"
  >
    <svg width="16" height="16" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.2 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.2 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 2.9-2.2 5.4-4.7 7.1l7.3 5.7c4.3-4 6.7-9.9 6.7-17.3z"/>
      <path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C1 16.7 0 20.3 0 24s1 7.3 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.3-5.7c-2 1.4-4.7 2.3-8.6 2.3-6.3 0-11.6-3.7-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
    {label}
  </button>
);

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success("Sessão iniciada");
      await refresh();
      const target = data.user.role === "customer" ? "/portal" : "/admin";
      nav(loc.state?.from || target, { replace: true });
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Entrar na conta" subtitle="Acede aos teus orçamentos, propostas e encomendas.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email"><input type="email" required data-testid="login-email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Palavra-passe"><input type="password" required data-testid="login-password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {err && <p className="text-destructive text-xs" data-testid="login-error">{err}</p>}
        <button disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
          {loading ? "…" : "Entrar"} <ArrowRight size={14} />
        </button>
        <div className="text-xs flex items-center justify-between text-muted-foreground">
          <Link className="link-underline" to="/recuperar-password" data-testid="login-forgot">Esqueci-me da palavra-passe</Link>
          <Link className="link-underline" to="/registo" data-testid="login-register">Criar conta</Link>
        </div>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex-1 border-t border-border" /> ou <span className="flex-1 border-t border-border" />
      </div>
      <GoogleButton label="Continuar com Google" />
    </AuthShell>
  );
}

export function Register() {
  const [f, setF] = useState({
    name: "", email: "", password: "", phone: "",
    customer_type: "individual", company_name: "", vat_number: "",
    consent_gdpr: false, consent_email: true, consent_whatsapp: false,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data.user);
      toast.success("Conta criada");
      nav("/portal", { replace: true });
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Criar conta" subtitle="Poucos segundos. Depois podes pedir orçamentos e enviar ficheiros.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => set("customer_type", "individual")} className={`text-xs py-2 border ${f.customer_type === "individual" ? "border-brand bg-brand/5" : "border-border"}`} data-testid="register-type-individual">Particular</button>
          <button type="button" onClick={() => set("customer_type", "business")} className={`text-xs py-2 border ${f.customer_type === "business" ? "border-brand bg-brand/5" : "border-border"}`} data-testid="register-type-business">Empresa</button>
        </div>
        <Field label="Nome completo *"><input required data-testid="register-name" className="input" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Email *"><input type="email" required data-testid="register-email" className="input" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Palavra-passe *"><input type="password" required minLength={8} data-testid="register-password" className="input" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="mín. 8 caracteres" /></Field>
        <Field label="Telefone"><input data-testid="register-phone" className="input" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        {f.customer_type === "business" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome da empresa *"><input required data-testid="register-company" className="input" value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
            <Field label="NIF"><input data-testid="register-vat" className="input" value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} /></Field>
          </div>
        )}
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input type="checkbox" data-testid="register-consent-gdpr" checked={f.consent_gdpr} onChange={(e) => set("consent_gdpr", e.target.checked)} required />
          <span>Aceito a política de privacidade e o tratamento dos meus dados (RGPD).</span>
        </label>
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={f.consent_email} onChange={(e) => set("consent_email", e.target.checked)} />
          <span>Aceito receber comunicações por email sobre o meu pedido.</span>
        </label>
        {err && <p className="text-destructive text-xs" data-testid="register-error">{err}</p>}
        <button disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit">
          {loading ? "…" : "Criar conta"} <ArrowRight size={14} />
        </button>
        <p className="text-xs text-center text-muted-foreground">
          Já tens conta? <Link to="/login" className="link-underline">Entrar</Link>
        </p>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex-1 border-t border-border" /> ou <span className="flex-1 border-t border-border" />
      </div>
      <GoogleButton label="Continuar com Google" />
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch { setSent(true); }
    setLoading(false);
  }
  return (
    <AuthShell title="Recuperar palavra-passe" subtitle="Enviaremos um link para o seu email.">
      {sent ? (
        <p className="text-sm">Se existir uma conta com esse email, enviámos-lhe instruções para recuperar a palavra-passe.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><input required type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></Field>
          <button disabled={loading} className="btn-primary w-full justify-center" data-testid="forgot-submit">Enviar link</button>
          <p className="text-xs text-center"><Link className="link-underline" to="/login">Voltar</Link></p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  async function submit(e) {
    e.preventDefault(); setLoading(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  }

  return (
    <AuthShell title="Definir nova palavra-passe">
      {done ? (
        <>
          <p className="text-sm">Palavra-passe atualizada com sucesso.</p>
          <Link to="/login" className="btn-primary mt-6 w-full justify-center">Entrar</Link>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nova palavra-passe (mín. 8 caracteres)">
            <input required minLength={8} type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          </Field>
          {err && <p className="text-destructive text-xs">{err}</p>}
          <button disabled={loading || !token} className="btn-primary w-full justify-center" data-testid="reset-submit">Guardar</button>
        </form>
      )}
    </AuthShell>
  );
}

export function AuthCallback() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const processed = React.useRef(false);
  React.useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const session_id = params.get("session_id");
    if (!session_id) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        await api.post("/auth/session", { session_id });
        await refresh();
        toast.success("Sessão iniciada com Google");
        window.history.replaceState({}, "", "/portal");
        nav("/portal", { replace: true });
      } catch (e) {
        toast.error("Não foi possível iniciar sessão com Google");
        nav("/login", { replace: true });
      }
    })();
  }, [nav, refresh]);
  return <div className="min-h-screen flex items-center justify-center tech-label">a iniciar sessão…</div>;
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 cad-grid opacity-20" />
        <div className="relative p-14 h-full flex flex-col justify-between">
          <Link to="/"><Logo inverted size={80} /></Link>
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tighter">
              Da ideia ao <br /><span className="text-brand">produto físico</span>.
            </h2>
            <p className="mt-4 text-background/70 max-w-md text-sm">
              Modelação 3D, prototipagem e fabrico aditivo em Barcelos, Portugal.
            </p>
            <div className="mt-10 mono text-[10px] uppercase tracking-widest text-background/60">
              cadditive · V.2026.01 · ISO ready
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden block mb-8"><Logo size={64} /></Link>
          <h1 className="text-3xl font-bold tracking-tighter">{title}</h1>
          {subtitle && <p className="text-muted-foreground text-sm mt-2">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.6rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:var(--brand)}`}</style>
    </div>
  );
}
const Field = ({ label, children }) => (
  <label className="block">
    <span className="tech-label block mb-2">{label}</span>
    {children}
  </label>
);
